import { useState, type MouseEvent } from "react";
import type { HealthBucket } from "../../hooks/useAdminStatus";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "./chart/ChartTooltip";
import { bucketRangeLabel } from "../../lib/chartBuckets";
import styles from "./ChannelActivityChart.module.css";

type ChannelActivityChartProps = {
  readonly buckets: ReadonlyArray<HealthBucket>;
  readonly bucketSeconds: number;
};

type Hover = ChartTooltipAnchor & { readonly bucket: HealthBucket };

const cellAria = (bucket: HealthBucket, bucketSeconds: number): string =>
  bucket.activity
    ? `${bucketRangeLabel(bucket.t, bucketSeconds)}: channel activity`
    : `${bucketRangeLabel(bucket.t, bucketSeconds)}: no activity`;

// Sparse presence strip: one cell per bucket, an accent tick where a message
// flowed (in or out), a faint slot otherwise. A lone active cell gets extra
// width + glow so 1/48 stays visible.
export const ChannelActivityChart = ({
  buckets,
  bucketSeconds,
}: ChannelActivityChartProps): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);

  const onEnter = (bucket: HealthBucket) => (e: MouseEvent<HTMLSpanElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ bucket, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };

  return (
    <div className={styles.strip}>
      {buckets.map((bucket) => (
        <span
          key={bucket.t}
          className={`${styles.cell} ${bucket.activity ? styles.active : styles.idle}`}
          aria-label={cellAria(bucket, bucketSeconds)}
          onMouseEnter={onEnter(bucket)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
      {hover != null && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            {hover.bucket.activity && <span className={`${tip.tipDot} ${styles.tipDotAccent}`} aria-hidden />}
            <span className={tip.tipState}>
              {hover.bucket.activity ? "Channel activity" : "No activity"}
            </span>
            <span className={tip.tipTime}>{bucketRangeLabel(hover.bucket.t, bucketSeconds)}</span>
          </div>
          <div className={tip.tipBody}>
            {hover.bucket.activity
              ? "Inbound or outbound message in this window"
              : "No activity"}
          </div>
        </ChartTooltip>
      )}
    </div>
  );
};
